import { Label } from "@/components/ui/label";
import type { PickerOption } from "@/lib/liveavatar/options";
import { cn } from "@/lib/utils";
import { Check, ChevronLeft, ChevronRight, ImageOff, Loader2, Plus, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

const ACCEPTED = "image/png,image/jpeg";

/**
 * Visual background picker, styled like `AvatarPicker`: a scrollable row of
 * tiles plus a dashed "+" tile that uploads a new image. The upload itself is
 * owned by the caller (`onUpload`), which returns the hosted URL the provider
 * will fetch.
 */
export function BackgroundPicker({
  value,
  onChange,
  label = "Background",
  helper,
  options,
  customUrl,
  customId,
  onUpload,
  onRemoveCustom,
  uploadDisabled,
  uploadComingSoon,
}: {
  /** Currently selected background id. */
  value?: string;
  onChange: (id: string) => void;
  label?: string;
  helper?: string;
  /** Built-in tiles. The first one is expected to be the "none" option. */
  options: PickerOption[];
  /** Preview URL of the uploaded background, when there is one. */
  customUrl?: string;
  /** Id assigned to the uploaded background tile. */
  customId: string;
  /** Uploads the file and resolves with the hosted image URL. */
  onUpload: (file: File) => Promise<void>;
  onRemoveCustom: () => void;
  uploadDisabled?: boolean;
  /** Renders the upload tile disabled with a "Coming soon" badge. */
  uploadComingSoon?: boolean;
}) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const tiles: PickerOption[] = customUrl
    ? [...options, { id: customId, label: "Uploaded", preview_url: customUrl }]
    : options;

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => {
      setCanScrollLeft(el.scrollLeft > 2);
      setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", update);
      ro.disconnect();
    };
  }, [tiles.length]);

  function scrollBy(dir: 1 | -1) {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: "smooth" });
  }

  async function handleFile(file?: File | null) {
    if (!file) return;
    setUploadError(null);
    setUploading(true);
    try {
      await onUpload(file);
    } catch (err) {
      setUploadError((err as Error)?.message || "Could not upload this image.");
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {helper && <p className="text-xs text-muted-foreground">{helper}</p>}
      <div className="relative">
        <div
          ref={scrollRef}
          className="flex gap-2 overflow-x-auto overflow-y-hidden rounded-lg border bg-muted/20 p-2 snap-x [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        >
          {tiles.map((o) => {
            const selected = o.id === value;
            const isCustom = o.id === customId;
            return (
              <div key={o.id} className="relative shrink-0">
                <button
                  type="button"
                  onClick={() => onChange(o.id)}
                  className={cn(
                    "group relative flex w-40 shrink-0 snap-start flex-col overflow-hidden rounded-lg border bg-card text-left transition-all hover:shadow-md",
                    selected
                      ? "border-primary ring-2 ring-primary"
                      : "border-border hover:border-primary/40",
                  )}
                >
                  <div className="relative aspect-video w-full overflow-hidden bg-muted">
                    {o.preview_url ? (
                      <img
                        src={o.preview_url}
                        alt={o.label}
                        loading="lazy"
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                        <ImageOff className="h-5 w-5" />
                      </div>
                    )}
                    {selected && (
                      <span className="absolute right-1 top-1 rounded-full bg-primary p-0.5 text-primary-foreground shadow">
                        <Check className="h-2.5 w-2.5" />
                      </span>
                    )}
                  </div>
                  <div className="px-1.5 py-1">
                    <p className="truncate text-[11px] font-medium leading-tight">{o.label}</p>
                  </div>
                </button>
                {isCustom && (
                  <button
                    type="button"
                    onClick={onRemoveCustom}
                    aria-label="Remove uploaded background"
                    className="absolute left-1 top-1 rounded-full bg-background/90 p-1 text-muted-foreground opacity-0 shadow ring-1 ring-border transition-opacity hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100 [div:hover>&]:opacity-100"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
            );
          })}
          <button
            type="button"
            onClick={() => {
              if (uploadComingSoon) return;
              inputRef.current?.click();
            }}
            disabled={uploading || uploadDisabled || uploadComingSoon}
            aria-disabled={uploadComingSoon || undefined}
            className={cn(
              "relative flex w-40 shrink-0 snap-start flex-col items-center justify-center gap-1 rounded-lg border border-dashed bg-card/40 text-muted-foreground transition-colors",
              "aspect-video hover:border-primary/50 hover:text-foreground disabled:opacity-50",
              uploadComingSoon && "cursor-not-allowed hover:border-border hover:text-muted-foreground",
            )}
          >
            {uploading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Plus className="h-5 w-5" />
            )}
            <span className="text-[11px] font-medium">
              {uploading ? "Uploading…" : "Upload image"}
            </span>
            {uploadComingSoon && (
              <span className="rounded-full border border-border bg-muted px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">
                Coming soon
              </span>
            )}
          </button>
          {!uploadComingSoon && (
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPTED}
            className="hidden"
            onChange={(e) => {
              void handleFile(e.target.files?.[0]);
              e.target.value = "";
            }}
          />
          )}
        </div>
        {canScrollLeft && (
          <button
            type="button"
            onClick={() => scrollBy(-1)}
            aria-label="Scroll left"
            className="absolute left-1 top-1/2 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-full bg-background/95 text-foreground shadow-md ring-1 ring-border hover:bg-background"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
        )}
        {canScrollRight && (
          <button
            type="button"
            onClick={() => scrollBy(1)}
            aria-label="Scroll right"
            className="absolute right-1 top-1/2 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-full bg-background/95 text-foreground shadow-md ring-1 ring-border hover:bg-background"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        )}
      </div>
      {uploadError && <p className="text-xs text-destructive">{uploadError}</p>}
    </div>
  );
}